import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { auth, db } from "@/lib/firebase";
import { orderBy, doc, getDoc } from "firebase/firestore";
import { dualReadJobs } from "@/lib/firestore-helpers";
import { useAuth } from "@/contexts/AuthContext";
import { StatusBadge } from "@/components/StatusBadge";
import { ComplianceStatusBadge } from "@/components/compliance";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/PageHeader";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Plus, MapPin, Building2, Loader2, Play, ChevronDown, AlertTriangle, CheckCircle2, FileText, Wrench, ClipboardList, ArrowRight } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { sanitizeErrorMessage } from "@/lib/utils";
import { useRunComplianceCheck } from "@/hooks/useCompliance";
import { useAvailableStates, useStateByCode, useCountiesByState, useJurisdictions } from "@/hooks/useJurisdictions";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

type ComplianceStatus = 'pass' | 'fail' | 'needs_review' | 'not_started' | null;

type ComplianceSummary = {
  critical: number;
  warning: number;
  info: number;
  total: number;
};

type InspectionStatus = 'pending_verification' | 'verified' | 'scheduled' | 'passed' | 'failed';

type Job = {
  id: string;
  title: string;
  description: string;
  location: string;
  status: "not_started" | "in_progress" | "completed" | "pending_review";
  client_name: string;
  created_at: string;
  property_id: string | null;
  properties?: {
    name: string;
    address: string;
  } | null;
  // Compliance fields
  complianceStatus?: ComplianceStatus;
  complianceScore?: number | null;
  jurisdictionId?: number | null;
  permitType?: string;
  complianceSummary?: ComplianceSummary | null;
  lastComplianceCheck?: string | null;
  // Inspection fields
  inspectionStatus?: InspectionStatus | null;
  submittedForPermitsAt?: string | null;
};

const Jobs = () => {
  const [jobs, setJobs] = useState<Job[]>([]);
  const [properties, setProperties] = useState<Array<{ id: string; name: string }>>([]);
  const [selectedProperty, setSelectedProperty] = useState<string>("all");
  // Location/jurisdiction filters
  const [selectedStateCode, setSelectedStateCode] = useState<string>("FL");
  const [selectedCountyId, setSelectedCountyId] = useState<number | null>(null);
  const [selectedJurisdictionId, setSelectedJurisdictionId] = useState<string | null>(null);
  // New trade and status filters
  const [selectedTrade, setSelectedTrade] = useState<string>("all");
  const [selectedStatus, setSelectedStatus] = useState<string>("all");

  const [loading, setLoading] = useState(true);
  const [runningCheckJobId, setRunningCheckJobId] = useState<string | null>(null);
  const [expandedJobId, setExpandedJobId] = useState<string | null>(null);
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user, profile: userProfile, loading: authLoading } = useAuth();

  const runComplianceCheck = useRunComplianceCheck();

  // Jurisdiction data hooks
  const { data: states = [] } = useAvailableStates();
  const { data: selectedState } = useStateByCode(selectedStateCode);
  const { data: counties = [] } = useCountiesByState(selectedState?.id || null);
  const { data: jurisdictions = [] } = useJurisdictions({
    stateCode: selectedStateCode,
    countyId: selectedCountyId ?? undefined,
  });

  useEffect(() => {
    // Wait for auth to finish loading
    if (authLoading) return;

    // If no user, redirect to auth
    if (!user) {
      navigate("/auth");
      return;
    }

    // Wait for userProfile to load from AuthContext
    if (!userProfile) return;

    // Get the contractorId from user profile
    // For org users, this will be the orgId; for regular users, it's their own uid
    const contractorId = userProfile.contractorId || user.uid;
    fetchJobs(contractorId);
  }, [navigate, user, userProfile, authLoading]);

  const fetchJobs = async (userId: string) => {
    try {
      // Fetch jobs for this contractor using dual-read
      const jobsSnapshot = await dualReadJobs(userId, [
        orderBy("createdAt", "desc")
      ]);
      const jobsData = await Promise.all(
        jobsSnapshot.docs
          // Filter out soft-deleted jobs
          .filter((jobDoc) => {
            const jobData = jobDoc.data();
            return !jobData.isDeleted && !jobData.deletedAt;
          })
          .map(async (jobDoc) => {
          const jobData = jobDoc.data();
          let propertyData = null;

          // Fetch property if it exists
          if (jobData.property_id) {
            const propertyDoc = await getDoc(doc(db, "properties", jobData.property_id));
            if (propertyDoc.exists()) {
              const data = propertyDoc.data();
              propertyData = {
                name: data.name,
                address: data.address,
              };
            }
          }

          return {
            id: jobDoc.id,
            title: jobData.title,
            description: jobData.description || "",
            location: jobData.location || "",
            status: jobData.status,
            client_name: jobData.client_name || "",
            created_at: jobData.createdAt?.toDate().toISOString() || "",
            property_id: jobData.property_id || null,
            properties: propertyData,
            // Compliance fields
            complianceStatus: jobData.complianceStatus || null,
            complianceScore: jobData.complianceScore ?? null,
            jurisdictionId: jobData.jurisdictionId || null,
            permitType: jobData.permitType || "",
            complianceSummary: jobData.complianceSummary || null,
            lastComplianceCheck: jobData.lastComplianceCheck?.toDate?.()?.toISOString() || null,
            // Inspection fields
            inspectionStatus: jobData.inspectionStatus || null,
            submittedForPermitsAt: jobData.submittedForPermitsAt || null,
          };
        })
      );

      setJobs(jobsData);

      // Extract unique properties for filter
      const uniqueProperties = Array.from(
        new Map(
          jobsData
            ?.filter(job => job.properties)
            .map(job => [job.property_id, { id: job.property_id!, name: job.properties!.name }])
        ).values()
      );
      setProperties(uniqueProperties);
    } catch (error: any) {
      console.error("Error loading jobs:", error);
      toast({
        title: "Error loading jobs",
        description: sanitizeErrorMessage(error, "Failed to load jobs. Please try again."),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Filter by property
  let filteredJobs = selectedProperty === "all"
    ? jobs
    : jobs.filter(job => job.property_id === selectedProperty);

  // Filter by jurisdiction
  if (selectedJurisdictionId && selectedJurisdictionId !== "all") {
    filteredJobs = filteredJobs.filter(
      job => job.jurisdictionId?.toString() === selectedJurisdictionId
    );
  }

  // Filter by trade/permit type
  if (selectedTrade !== "all") {
    filteredJobs = filteredJobs.filter(
      job => job.permitType === selectedTrade
    );
  }

  // Filter by combined status (job status + inspection status)
  if (selectedStatus !== "all") {
    filteredJobs = filteredJobs.filter(job => {
      switch (selectedStatus) {
        // Job workflow statuses
        case 'not_started':
        case 'in_progress':
        case 'completed':
        case 'pending_review':
          return job.status === selectedStatus;
        // Inspection workflow statuses
        case 'pending_check':
          return !job.complianceStatus || job.complianceStatus === 'not_started';
        case 'pending_pre_inspection':
          return job.inspectionStatus === 'pending_verification';
        case 'submitted':
          return job.inspectionStatus === 'verified' || job.inspectionStatus === 'scheduled';
        case 'inspection_passed':
          return job.inspectionStatus === 'passed';
        case 'inspection_failed':
          return job.inspectionStatus === 'failed';
        default:
          return true;
      }
    });
  }

  // Handle running compliance check
  const handleRunCheck = async (e: React.MouseEvent, job: Job) => {
    e.stopPropagation(); // Prevent card click navigation

    if (!job.jurisdictionId) {
      toast({
        title: "Cannot run compliance check",
        description: "This job doesn't have a jurisdiction set. Edit the job to add location details.",
        variant: "destructive",
      });
      return;
    }

    const currentUser = auth.currentUser;
    if (!currentUser) return;

    setRunningCheckJobId(job.id);

    // Use the proper contractorId (orgId for org users, uid for regular users)
    const contractorId = userProfile?.contractorId ||
                        (userProfile?.isPrimaryAdmin ? currentUser.uid : currentUser.uid);

    try {
      const result = await runComplianceCheck.mutateAsync({
        jobId: job.id,
        contractorId: contractorId,
        permitType: job.permitType,
      });

      // Update the job in local state with new compliance data
      setJobs(prevJobs => prevJobs.map(j =>
        j.id === job.id
          ? {
              ...j,
              complianceStatus: result.status,
              complianceScore: result.score,
              complianceSummary: result.summary,
              lastComplianceCheck: new Date().toISOString()
            }
          : j
      ));

      // Auto-expand the job to show results
      setExpandedJobId(job.id);

      toast({
        title: "Compliance check complete",
        description: `Score: ${result.score}% - ${result.status === 'pass' ? 'Compliant' : result.status === 'fail' ? 'Non-Compliant' : 'Needs Review'}`,
        variant: result.status === 'pass' ? 'default' : 'destructive',
      });
    } catch (error: any) {
      toast({
        title: "Compliance check failed",
        description: sanitizeErrorMessage(error, "Failed to run compliance check. Please try again."),
        variant: "destructive",
      });
    } finally {
      setRunningCheckJobId(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <PageHeader
        title="Permits"
        actions={
          <Button
            size="sm"
            onClick={() => navigate("/jobs/new")}
            className="h-8 sm:h-9"
          >
            <Plus className="h-4 w-4 mr-1" />
            <span className="hidden xs:inline">New Project</span>
            <span className="xs:hidden">New</span>
          </Button>
        }
      />
      <div className="p-3 sm:p-4 space-y-3 sm:space-y-4">
        {/* Filters */}
        <Card>
          <CardContent className="pt-4 sm:pt-6">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 sm:gap-3">
              {/* State Filter */}
              <div className="flex items-center gap-2">
                <MapPin className="h-4 w-4 text-muted-foreground shrink-0 hidden sm:block" />
                <Select
                  value={selectedStateCode}
                  onValueChange={(value) => {
                    setSelectedStateCode(value);
                    setSelectedCountyId(null);
                    setSelectedJurisdictionId(null);
                  }}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="State" />
                  </SelectTrigger>
                  <SelectContent>
                    {states.map((state) => (
                      <SelectItem key={state.code} value={state.code}>
                        {state.code}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* County Filter */}
              <div className="flex items-center gap-2">
                <Building2 className="h-4 w-4 text-muted-foreground shrink-0 hidden sm:block" />
                <Select
                  value={selectedCountyId?.toString() || "all"}
                  onValueChange={(value) => {
                    setSelectedCountyId(value === "all" ? null : parseInt(value));
                    setSelectedJurisdictionId(null);
                  }}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="County" />
                  </SelectTrigger>
                  <SelectContent className="max-h-[280px]">
                    <SelectItem value="all">All Counties</SelectItem>
                    {counties.map((county) => (
                      <SelectItem key={county.id} value={county.id.toString()}>
                        {county.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Jurisdiction Filter */}
              <div className="flex items-center gap-2">
                <Select
                  value={selectedJurisdictionId || "all"}
                  onValueChange={(value) => setSelectedJurisdictionId(value === "all" ? null : value)}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Jurisdiction" />
                  </SelectTrigger>
                  <SelectContent className="max-h-[280px]">
                    <SelectItem value="all">All Jurisdictions</SelectItem>
                    {jurisdictions.map((j) => (
                      <SelectItem key={j.id} value={j.id.toString()}>
                        {j.ahj_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Trade/Permit Type Filter */}
              <div className="flex items-center gap-2">
                <Wrench className="h-4 w-4 text-muted-foreground shrink-0 hidden sm:block" />
                <Select value={selectedTrade} onValueChange={setSelectedTrade}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Trade" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Trades</SelectItem>
                    <SelectItem value="electrical">Electrical</SelectItem>
                    <SelectItem value="plumbing">Plumbing</SelectItem>
                    <SelectItem value="mechanical">HVAC</SelectItem>
                    <SelectItem value="roofing">Roofing</SelectItem>
                    <SelectItem value="building">General/Building</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Combined Status Filter */}
              <div className="flex items-center gap-2">
                <ClipboardList className="h-4 w-4 text-muted-foreground shrink-0 hidden sm:block" />
                <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="not_started">Not Started</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="pending_review">Pending Review</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="pending_check">Needs Compliance Check</SelectItem>
                    <SelectItem value="pending_pre_inspection">Pre-Inspection Pending</SelectItem>
                    <SelectItem value="submitted">Submitted for Inspection</SelectItem>
                    <SelectItem value="inspection_passed">Inspection Passed</SelectItem>
                    <SelectItem value="inspection_failed">Inspection Failed</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Property Filter */}
              {properties.length > 0 && (
                <div className="flex items-center gap-2">
                  <Select value={selectedProperty} onValueChange={setSelectedProperty}>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Property" />
                    </SelectTrigger>
                    <SelectContent className="max-h-[280px]">
                      <SelectItem value="all">All Properties</SelectItem>
                      {properties.map((property) => (
                        <SelectItem key={property.id} value={property.id}>
                          {property.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {filteredJobs.length === 0 ? (
          <Card>
            <CardContent className="pt-6 text-center">
              <p className="text-muted-foreground mb-4">No projects yet</p>
              <Button onClick={() => navigate("/jobs/new")}>
                <Plus className="mr-2 h-4 w-4" />
                Create First Project
              </Button>
            </CardContent>
          </Card>
        ) : (
          filteredJobs.map((job) => (
            <Card
              key={job.id}
              className="hover:shadow-lg transition-shadow"
            >
              <CardHeader
                className="cursor-pointer p-4 sm:p-6"
                onClick={() => navigate(`/jobs/${job.id}`)}
              >
                <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-2 sm:gap-4">
                  <div className="flex-1 min-w-0">
                    <CardTitle className="text-base sm:text-lg truncate">{job.title}</CardTitle>
                    {job.properties && (
                      <div className="flex items-center gap-1 text-sm text-muted-foreground mt-1">
                        <Building2 className="h-3 w-3" />
                        {job.properties.name}
                      </div>
                    )}
                    {job.client_name && (
                      <p className="text-sm text-muted-foreground mt-1">
                        Client: {job.client_name}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-row sm:flex-col items-center sm:items-end gap-2">
                    <StatusBadge status={job.status} />
                    <div
                      onClick={(e) => {
                        e.stopPropagation();
                        if (job.complianceStatus && job.complianceStatus !== 'not_started') {
                          setExpandedJobId(expandedJobId === job.id ? null : job.id);
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && job.complianceStatus && job.complianceStatus !== 'not_started') {
                          e.stopPropagation();
                          setExpandedJobId(expandedJobId === job.id ? null : job.id);
                        }
                      }}
                      role={job.complianceStatus && job.complianceStatus !== 'not_started' ? 'button' : undefined}
                      tabIndex={job.complianceStatus && job.complianceStatus !== 'not_started' ? 0 : undefined}
                      aria-expanded={job.complianceStatus && job.complianceStatus !== 'not_started' ? expandedJobId === job.id : undefined}
                      className={job.complianceStatus && job.complianceStatus !== 'not_started' ? 'cursor-pointer' : ''}
                    >
                      <ComplianceStatusBadge
                        status={job.complianceStatus || 'not_started'}
                        score={job.complianceScore}
                        size="sm"
                      />
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-2 p-4 sm:p-6 pt-0 sm:pt-0">
                {job.description && (
                  <p className="text-sm text-muted-foreground line-clamp-2 cursor-pointer" onClick={() => navigate(`/jobs/${job.id}`)}>
                    {job.description}
                  </p>
                )}

                {/* Expandable Compliance Summary */}
                <Collapsible open={expandedJobId === job.id}>
                  <CollapsibleContent className="pt-2">
                    {job.complianceSummary && (
                      <div className="p-3 bg-muted rounded-lg space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium">Compliance Summary</span>
                          {job.lastComplianceCheck && (
                            <span className="text-xs text-muted-foreground">
                              Checked {new Date(job.lastComplianceCheck).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-center">
                          {job.complianceSummary.critical > 0 && (
                            <div className="p-2 bg-red-100 dark:bg-red-950 rounded">
                              <div className="flex items-center justify-center gap-1">
                                <AlertTriangle className="h-3 w-3 text-red-600" />
                                <span className="text-lg font-bold text-red-600">{job.complianceSummary.critical}</span>
                              </div>
                              <p className="text-xs text-red-600">Critical</p>
                            </div>
                          )}
                          {job.complianceSummary.warning > 0 && (
                            <div className="p-2 bg-yellow-100 dark:bg-yellow-950 rounded">
                              <div className="flex items-center justify-center gap-1">
                                <AlertTriangle className="h-3 w-3 text-yellow-600" />
                                <span className="text-lg font-bold text-yellow-600">{job.complianceSummary.warning}</span>
                              </div>
                              <p className="text-xs text-yellow-600">Warnings</p>
                            </div>
                          )}
                          {job.complianceStatus === 'pass' && (
                            <div className="p-2 bg-green-100 dark:bg-green-950 rounded col-span-3">
                              <div className="flex items-center justify-center gap-1">
                                <CheckCircle2 className="h-4 w-4 text-green-600" />
                                <span className="text-sm font-medium text-green-600">All checks passed</span>
                              </div>
                            </div>
                          )}
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-full mt-2"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/jobs/${job.id}`);
                          }}
                        >
                          <FileText className="h-3 w-3 mr-1" />
                          View Full Report
                        </Button>
                      </div>
                    )}
                  </CollapsibleContent>
                </Collapsible>

                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
                  {job.location && (
                    <div className="flex items-center text-xs sm:text-sm text-muted-foreground cursor-pointer truncate max-w-full" onClick={() => navigate(`/jobs/${job.id}`)}>
                      <MapPin className="h-3 w-3 sm:h-4 sm:w-4 mr-1 shrink-0" />
                      <span className="truncate">{job.location}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2 self-end sm:self-auto">
                    {/* Expand/Collapse button */}
                    {job.complianceStatus && job.complianceStatus !== 'not_started' && job.complianceSummary && (
                      <CollapsibleTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            setExpandedJobId(expandedJobId === job.id ? null : job.id);
                          }}
                          className="shrink-0"
                        >
                          <ChevronDown className={`h-4 w-4 transition-transform ${expandedJobId === job.id ? 'rotate-180' : ''}`} />
                        </Button>
                      </CollapsibleTrigger>
                    )}
                    {/* Continue Workflow Button - navigates to wizard (primary route) */}
                    <Button
                      variant="default"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/jobs/${job.id}`);
                      }}
                      className="shrink-0"
                      aria-label={`Continue workflow for ${job.title}`}
                    >
                      <ArrowRight className="h-3 w-3 mr-1" aria-hidden="true" />
                      <span className="hidden sm:inline">Continue</span>
                      <span className="sm:hidden">Go</span>
                    </Button>
                    {/* Run Check Button */}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => handleRunCheck(e, job)}
                      disabled={runningCheckJobId === job.id}
                      className="shrink-0"
                      aria-label={runningCheckJobId === job.id ? `Running compliance check for ${job.title}` : `Run compliance check for ${job.title}`}
                    >
                      {runningCheckJobId === job.id ? (
                        <>
                          <Loader2 className="h-3 w-3 mr-1 animate-spin" aria-hidden="true" />
                          <span>Checking...</span>
                        </>
                      ) : (
                        <>
                          <Play className="h-3 w-3 mr-1" aria-hidden="true" />
                          <span>Run Check</span>
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

export default Jobs;
